import {Dispatch, memo, ReactNode, SetStateAction} from 'react';

import {Icon} from '@mui/material';
import {Box} from '@mui/system';
import {useTranslation} from 'react-i18next';

import {
  AppButton,
  AppButtonVariant,
  ButtonContainer,
} from '@/app/shared-components';
import InputTextField, {
  TransformTypeInputValues,
} from '@/app/shared-components/InputTextField/InputTextField';
import {COLORS} from '@/styles/colors';
import {validateNameInput} from '@/utils/validateNames';

interface SignUpStep1FormFragmentProps {
  firstName: string;
  lastName: string;
  signUpCode: string;
  showInputReferralCode: boolean;
  isMobile: boolean;
  isValidSignUpCode: boolean;
  isDisabledSignUpStep1: boolean;
  isLoadingAmplifyAuth: boolean;
  compatibilityBanner: ReactNode;
  setFirstName: Dispatch<SetStateAction<string>>;
  setLastName: Dispatch<SetStateAction<string>>;
  setSignUpCode: Dispatch<SetStateAction<string>>;
  setShowInputReferralCode: Dispatch<SetStateAction<boolean>>;
  handleOnClickSignUpStep1: () => void;
  handleOnClickSignIn: () => void;
}

export const SignUpStep1FormFragment = memo(
  ({
    firstName,
    lastName,
    signUpCode,
    showInputReferralCode,
    isMobile,
    isValidSignUpCode,
    isDisabledSignUpStep1,
    isLoadingAmplifyAuth,
    compatibilityBanner,
    setFirstName,
    setLastName,
    setSignUpCode,
    setShowInputReferralCode,
    handleOnClickSignUpStep1,
    handleOnClickSignIn,
  }: SignUpStep1FormFragmentProps) => {
    const {t} = useTranslation();

    return (
      <Box className="signup-form-container">
        {compatibilityBanner}
        <form className="signup-form">
          <Box className="names-content">
            <InputTextField
              id="SignUpPage_firstNameInput"
              label={t('sign_up.first_name')}
              placeholder={t('sign_up.first_name')}
              maxLength={50}
              value={firstName}
              setValue={(value: string) =>
                setFirstName(validateNameInput(value))
              }
              onBlur={() => setFirstName(firstName.trim())}
              style={{paddingRight: '2.5px'}}
              labelBackgroundColor={COLORS.AUTH_BACKGROUND}
            />
            <InputTextField
              id="SignUpPage_lastNameInput"
              label={t('sign_up.last_name')}
              placeholder={t('sign_up.last_name')}
              maxLength={50}
              value={lastName}
              setValue={(value: string) =>
                setLastName(validateNameInput(value))
              }
              onBlur={() => setLastName(lastName.trim())}
              style={{paddingLeft: '2.5px'}}
              labelBackgroundColor={COLORS.AUTH_BACKGROUND}
            />
          </Box>
          {isMobile && !showInputReferralCode && (
            <Box className="referral-button-container">
              <AppButton
                id="SignUpPage_addReferralCodeButton"
                iconLeft={
                  <Icon style={{fontSize: 20, color: COLORS.ON_SURFACE_1}}>
                    add
                  </Icon>
                }
                label={t('sign_up.add_referral_code')}
                onClick={() => {
                  setShowInputReferralCode(true);
                }}
                variant={AppButtonVariant.onText}
                labelColor={COLORS.ON_SURFACE_1}
                removeStyles={true}
              />
            </Box>
          )}
          {(!isMobile || showInputReferralCode) && (
            <InputTextField
              id="SignUpPage_referralCodeInput"
              label={t('sign_up.referral_code')}
              placeholder={t('sign_up.referral_code')}
              maxLength={50}
              value={signUpCode}
              setValue={setSignUpCode}
              error={!isValidSignUpCode}
              helperText={
                !isValidSignUpCode
                  ? t('sign_up.helper_text_referral_code')
                  : undefined
              }
              transformType={TransformTypeInputValues.UPPERCASE}
              labelBackgroundColor={COLORS.AUTH_BACKGROUND}
            />
          )}
        </form>

        <ButtonContainer
          sticky
          withoutBorder>
          {isMobile && (
            <AppButton
              id="SignUpPage_signInButton"
              label={t('sign_up.login')}
              variant={AppButtonVariant.onSecondary}
              isLoading={isLoadingAmplifyAuth}
              onClick={handleOnClickSignIn}
            />
          )}
          <AppButton
            id="SignUpPage_continueButton"
            label={t('sign_up.continue')}
            onClick={handleOnClickSignUpStep1}
            disabled={!!isDisabledSignUpStep1}
            isLoading={isLoadingAmplifyAuth}
          />
          {!isMobile && (
            <div className="referral-container">
              <AppButton
                id="SignUpPage_signInButton"
                label={t('sign_up.login')}
                variant={AppButtonVariant.onSecondary}
                isLoading={isLoadingAmplifyAuth}
                onClick={handleOnClickSignIn}
              />
            </div>
          )}
        </ButtonContainer>
        {!isMobile && (
          <div className="signup-disclaimers">
            <p>{t('authentication.footer.fdic')}</p>
            <p>{t('authentication.footer.based_on')}</p>
          </div>
        )}
      </Box>
    );
  },
);
